/**
 * Count-the-outs quiz: procedural hand+flop scenarios, multiple-choice
 * answer, zero LLM cost.
 *
 * Six scenario types — chosen because each out-count is unambiguous
 * and together they span the answer space {2, 4, 6, 8, 9, 15} so the
 * user can't just pattern-match the draw type to the answer:
 *
 *   - **Flush draw (9 outs)** — 2 of a suit in hand + 2 more on board.
 *   - **Open-ended straight draw (8 outs)** — 4-card consecutive window,
 *     5th card extends either end (4 + 4 = 8).
 *   - **Gutshot straight draw (4 outs)** — 4-card 5-rank window missing
 *     the middle card. Only the gut card completes (4 outs).
 *   - **Set draw with pocket pair (2 outs)** — pocket pair, board has
 *     no matching rank. 4 − 2 = 2.
 *   - **Two overcards on low board (6 outs)** — hero has 2 distinct
 *     broadway cards, board ranks all below hero's lowest, no draw
 *     interaction. 3 + 3 = 6.
 *   - **Combo flush + OESD (15 outs)** — both draws on the same board.
 *     9 flush + 8 straight − 2 (the two cards that complete both) = 15.
 *
 * Skipped (drag us toward villain-hand territory):
 *   - 12 outs (flush + 1 overcard or flush + gutshot) — needs care
 *     about whether the overcard/gut card is the same suit
 *   - 5 outs (pair → 2-pair/trips) — kicker-pairs-board is a footgun
 *   - "Outs to beat villain's specific hand" — separate quiz idea
 *
 * The question explicitly tells the user the target — "How many outs
 * to a flush?" — so the answer is unambiguous; we're not asking the
 * user to identify the draw, just count its outs.
 */

const RANKS = ["2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K", "A"] as const;
const SUITS = ["s", "h", "d", "c"] as const;

export type Rank = (typeof RANKS)[number];
export type Suit = (typeof SUITS)[number];
export type Card = `${Rank}${Suit}`;

export type DrawTarget =
  | "flush"
  | "straight"
  | "gutshot"
  | "set"
  | "overcards"
  | "combo";

export interface CountOutsQuestion {
  hand: [Card, Card];
  board: [Card, Card, Card];
  /** Which draw the question is asking about. */
  target: DrawTarget;
  /** Human-readable target prose ("a flush" / "the nut straight" / "a set"). */
  targetLabel: string;
  /** Correct number of outs. */
  outs: number;
  /** Multiple-choice options in display order. */
  options: number[];
  /** Index into `options` of the correct value. */
  correctIndex: number;
  /** Explanation shown on reveal — how the out count is derived. */
  rationale: string;
}

import { pickFrom, pickN, shuffle } from "@/features/quiz/lib/common";

const CANONICAL_OUT_COUNTS = [2, 4, 6, 8, 9, 12, 15] as const;

// --- Scenario generators ------------------------------------------------

function buildFlushDraw(rand: () => number): CountOutsQuestion {
  // Pick the suit hero is drawing in
  const drawSuit = pickFrom(SUITS, rand);
  const offSuits = SUITS.filter((s) => s !== drawSuit);

  // 4 of the 5 ranks-in-suit are used by hand+board; pick them
  const suitRanks = pickN(RANKS, 4, rand) as Rank[];
  const [hr1, hr2, br1, br2] = suitRanks;

  // 5th board card uses a different suit and a different rank
  const otherRank = pickFrom(
    RANKS.filter((r) => !suitRanks.includes(r)),
    rand,
  );
  const otherSuit = pickFrom(offSuits, rand);

  const hand: [Card, Card] = [`${hr1}${drawSuit}`, `${hr2}${drawSuit}`];
  const board: [Card, Card, Card] = [
    `${br1}${drawSuit}`,
    `${br2}${drawSuit}`,
    `${otherRank}${otherSuit}`,
  ];

  return {
    hand,
    board,
    target: "flush",
    targetLabel: "a flush",
    outs: 9,
    options: [],
    correctIndex: -1,
    rationale:
      "13 cards in each suit; 4 are accounted for (2 in your hand + 2 on the board). 13 − 4 = 9 outs.",
  };
}

/** Valid OESD windows — 4-card consecutive sequences where the 5th
 *  card on either end completes a straight. Lower-indexed window
 *  starts with the rank at index `i`. */
const OESD_START_INDICES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const; // 2345 through TJQK

function buildOESD(rand: () => number): CountOutsQuestion {
  const startIdx = pickFrom(OESD_START_INDICES, rand);
  const windowRanks: Rank[] = [
    RANKS[startIdx]!,
    RANKS[startIdx + 1]!,
    RANKS[startIdx + 2]!,
    RANKS[startIdx + 3]!,
  ];

  // Split 4 ranks → 2 to hero, 2 to board
  const shuffled = shuffle(windowRanks, rand);
  const heroRanks = [shuffled[0]!, shuffled[1]!];
  const boardWindowRanks = [shuffled[2]!, shuffled[3]!];

  // 3rd board card: a rank outside the window AND not extending the
  // straight on either end (so we don't accidentally turn the OESD
  // into a made straight). Also must not be inside the window already.
  const lowExtender = startIdx > 0 ? RANKS[startIdx - 1] : null;
  const highExtender = startIdx + 4 < RANKS.length ? RANKS[startIdx + 4] : null;
  // Special case: 2345 window — the Ace also makes the wheel
  const wheelExtender = startIdx === 0 ? "A" : null;
  const forbiddenRanks: (Rank | null)[] = [
    ...windowRanks,
    lowExtender,
    highExtender,
    wheelExtender as Rank | null,
  ];
  const allowedRanks = RANKS.filter((r) => !forbiddenRanks.includes(r));
  const otherRank = pickFrom(allowedRanks, rand);

  // Suit assignment: distribute 4 suits across 5 cards so no suit
  // appears 3+ times (would create a flush draw, which would confuse
  // the "draw to a straight" question). With 4 suits and 5 cards,
  // exactly one suit must appear twice; the other 3 once each.
  const shuffledSuits = shuffle(SUITS, rand);
  // hand: 2 different suits (positions 0, 1)
  // board: 2 different suits (positions 2, 3) — different from hand
  // 3rd board: one of the 4 suits (repeating exactly one)
  const handSuits: [Suit, Suit] = [shuffledSuits[0]!, shuffledSuits[1]!];
  const boardSuits: [Suit, Suit] = [shuffledSuits[2]!, shuffledSuits[3]!];
  const repeatedSuit = pickFrom(shuffledSuits, rand);

  const hand: [Card, Card] = [
    `${heroRanks[0]}${handSuits[0]}`,
    `${heroRanks[1]}${handSuits[1]}`,
  ];
  const board: [Card, Card, Card] = [
    `${boardWindowRanks[0]}${boardSuits[0]}`,
    `${boardWindowRanks[1]}${boardSuits[1]}`,
    `${otherRank}${repeatedSuit}`,
  ];

  return {
    hand,
    board,
    target: "straight",
    targetLabel: "an open-ended straight",
    outs: 8,
    options: [],
    correctIndex: -1,
    rationale:
      "Your 4 connected cards open both ends: 4 cards complete on the low end + 4 on the high end = 8 outs.",
  };
}

function buildSetDraw(rand: () => number): CountOutsQuestion {
  // Hero's pair rank
  const pairRank = pickFrom(RANKS, rand);
  const pairSuits = pickN(SUITS, 2, rand) as [Suit, Suit];

  // 3 board cards with 3 distinct ranks, none matching hero's pair
  const boardRanks = pickN(
    RANKS.filter((r) => r !== pairRank),
    3,
    rand,
  ) as [Rank, Rank, Rank];
  // Rainbow board for clarity (no flush draw on top of the set draw)
  const boardSuits = pickN(SUITS, 3, rand) as [Suit, Suit, Suit];

  const hand: [Card, Card] = [
    `${pairRank}${pairSuits[0]}`,
    `${pairRank}${pairSuits[1]}`,
  ];
  const board: [Card, Card, Card] = [
    `${boardRanks[0]}${boardSuits[0]}`,
    `${boardRanks[1]}${boardSuits[1]}`,
    `${boardRanks[2]}${boardSuits[2]}`,
  ];

  return {
    hand,
    board,
    target: "set",
    targetLabel: "a set",
    outs: 2,
    options: [],
    correctIndex: -1,
    rationale:
      "Your pocket pair has 4 cards of its rank in the deck; 2 are in your hand. 4 − 2 = 2 outs to make a set.",
  };
}

function buildGutshot(rand: () => number): CountOutsQuestion {
  // Pick a 5-card straight window (no wheel/broadway edge cases for v1).
  // The "gut" position is one of the middle three slots (positions 1,
  // 2, or 3 of the 5-card window). Removing position 0 or 4 would
  // produce an OESD (handled by buildOESD).
  const startIdx = Math.floor(rand() * 9); // windows starting at rank index 0..8 (2 through T)
  const windowRanks: Rank[] = [
    RANKS[startIdx]!,
    RANKS[startIdx + 1]!,
    RANKS[startIdx + 2]!,
    RANKS[startIdx + 3]!,
    RANKS[startIdx + 4]!,
  ];
  const gutPositionInWindow = 1 + Math.floor(rand() * 3); // 1, 2, or 3
  const presentRanks = windowRanks.filter((_, i) => i !== gutPositionInWindow);

  // Distribute 4 ranks → 2 to hero, 2 to board
  const shuffled = shuffle(presentRanks, rand);
  const heroRanks = [shuffled[0]!, shuffled[1]!];
  const boardWindowRanks = [shuffled[2]!, shuffled[3]!];

  // 3rd board card: not in the 5-card window, not extending either
  // end (so the user doesn't see a phantom OESD).
  const lowExtender = startIdx > 0 ? RANKS[startIdx - 1] : null;
  const highExtender = startIdx + 5 < RANKS.length ? RANKS[startIdx + 5] : null;
  const forbidden: (Rank | null)[] = [...windowRanks, lowExtender, highExtender];
  const otherRank = pickFrom(
    RANKS.filter((r) => !forbidden.includes(r)),
    rand,
  );

  // Suits: spread across 4 suits to avoid creating a flush draw on top.
  const shuffledSuits = shuffle(SUITS, rand);
  const handSuits: [Suit, Suit] = [shuffledSuits[0]!, shuffledSuits[1]!];
  const boardSuits: [Suit, Suit] = [shuffledSuits[2]!, shuffledSuits[3]!];
  const repeatedSuit = pickFrom(shuffledSuits, rand);

  const hand: [Card, Card] = [
    `${heroRanks[0]}${handSuits[0]}`,
    `${heroRanks[1]}${handSuits[1]}`,
  ];
  const board: [Card, Card, Card] = [
    `${boardWindowRanks[0]}${boardSuits[0]}`,
    `${boardWindowRanks[1]}${boardSuits[1]}`,
    `${otherRank}${repeatedSuit}`,
  ];

  return {
    hand,
    board,
    target: "gutshot",
    targetLabel: "a gutshot straight",
    outs: 4,
    options: [],
    correctIndex: -1,
    rationale:
      "Only one rank completes the straight, and there are 4 cards of that rank in the deck. 4 outs.",
  };
}

function buildTwoOvercards(rand: () => number): CountOutsQuestion {
  // Hero ranks: two distinct broadways (T J Q K A).
  const BROADWAYS: Rank[] = ["T", "J", "Q", "K", "A"];
  const heroRanks = pickN(BROADWAYS, 2, rand) as [Rank, Rank];

  // Board ranks: three distinct lows (2-7). All below hero's lowest
  // — guaranteed since hero is broadways and board is in 2-7.
  // We also want to avoid producing an OESD or gutshot from
  // hero+board, so pick board ranks spaced apart enough. Easiest:
  // restrict to {2, 3, 4, 5, 6, 7} and require all 3 are distinct.
  // The hero broadway cards can't possibly join a 2-7 set in a 5-rank
  // straight window (gap is too big), so we're safe.
  const LOW_RANKS: Rank[] = ["2", "3", "4", "5", "6", "7"];
  const boardRanks = pickN(LOW_RANKS, 3, rand) as [Rank, Rank, Rank];

  // Suits: rainbow + 1 repeat at most, no 3-of-a-suit (avoid flush draw).
  // Easiest: pick 4 distinct suits across 5 card slots, one suit will
  // repeat once but not twice.
  const shuffledSuits = shuffle(SUITS, rand);
  const heroSuits: [Suit, Suit] = [shuffledSuits[0]!, shuffledSuits[1]!];
  const boardSuits: [Suit, Suit, Suit] = [
    shuffledSuits[2]!,
    shuffledSuits[3]!,
    shuffledSuits[Math.floor(rand() * 4)]!, // any suit; max 2-of-suit guaranteed
  ];

  const hand: [Card, Card] = [
    `${heroRanks[0]}${heroSuits[0]}`,
    `${heroRanks[1]}${heroSuits[1]}`,
  ];
  const board: [Card, Card, Card] = [
    `${boardRanks[0]}${boardSuits[0]}`,
    `${boardRanks[1]}${boardSuits[1]}`,
    `${boardRanks[2]}${boardSuits[2]}`,
  ];

  return {
    hand,
    board,
    target: "overcards",
    targetLabel: "top pair (pair either overcard)",
    outs: 6,
    options: [],
    correctIndex: -1,
    rationale:
      "Each overcard has 3 unseen cards of its rank. 3 + 3 = 6 outs to pair up.",
  };
}

function buildCombo(rand: () => number): CountOutsQuestion {
  // Pick OESD window — same constraints as buildOESD, but additionally
  // require both extender ranks exist in the deck (so the overlap math
  // is 9 + 8 − 2 = 15). The wheel (2345 window) is excluded because
  // the low extender is the Ace, which IS in deck — actually it works
  // fine. We just exclude TJQK because the high extender is Ace, also
  // valid. Hmm — both edge windows work. Keep them.
  const startIdx = pickFrom(OESD_START_INDICES, rand);
  const windowRanks: Rank[] = [
    RANKS[startIdx]!,
    RANKS[startIdx + 1]!,
    RANKS[startIdx + 2]!,
    RANKS[startIdx + 3]!,
  ];

  // Pick the flush suit
  const flushSuit = pickFrom(SUITS, rand);
  const offSuits = SUITS.filter((s) => s !== flushSuit);

  // Hero ranks + board window ranks all in the flush suit
  const shuffledWindow = shuffle(windowRanks, rand);
  const heroRanks = [shuffledWindow[0]!, shuffledWindow[1]!];
  const boardWindowRanks = [shuffledWindow[2]!, shuffledWindow[3]!];

  // 3rd board card: rank not in window, not extending the window
  // (no phantom made straight), AND not in the flush suit (no 5-flush).
  const lowExtender = startIdx > 0 ? RANKS[startIdx - 1] : null;
  const highExtender = startIdx + 4 < RANKS.length ? RANKS[startIdx + 4] : null;
  const wheelExtender = startIdx === 0 ? "A" : null;
  const forbiddenRanks: (Rank | null)[] = [
    ...windowRanks,
    lowExtender,
    highExtender,
    wheelExtender as Rank | null,
  ];
  const otherRank = pickFrom(
    RANKS.filter((r) => !forbiddenRanks.includes(r)),
    rand,
  );
  const otherSuit = pickFrom(offSuits, rand);

  const hand: [Card, Card] = [
    `${heroRanks[0]}${flushSuit}`,
    `${heroRanks[1]}${flushSuit}`,
  ];
  const board: [Card, Card, Card] = [
    `${boardWindowRanks[0]}${flushSuit}`,
    `${boardWindowRanks[1]}${flushSuit}`,
    `${otherRank}${otherSuit}`,
  ];

  return {
    hand,
    board,
    target: "combo",
    targetLabel: "a flush OR a straight",
    outs: 15,
    options: [],
    correctIndex: -1,
    rationale:
      "9 flush outs + 8 straight outs − 2 cards that complete both = 15 outs. The two extender ranks in your flush suit count once, not twice.",
  };
}

// --- Public entry point -------------------------------------------------

const BUILDERS: ((rand: () => number) => CountOutsQuestion)[] = [
  buildFlushDraw,
  buildOESD,
  buildGutshot,
  buildSetDraw,
  buildTwoOvercards,
  buildCombo,
];

/** Build a fresh procedural question. Picks a scenario type uniformly
 *  at random, generates the scenario, attaches multiple-choice options. */
export function generateQuestion(
  rand: () => number = Math.random,
): CountOutsQuestion {
  const builder = pickFrom(BUILDERS, rand);
  const base = builder(rand);
  return attachOptions(base, rand);
}

function attachOptions(
  q: CountOutsQuestion,
  rand: () => number,
): CountOutsQuestion {
  // Distractors drawn from the canonical out-count set so wrong
  // answers are meaningful (a player who confused a flush draw for
  // an OESD would pick 8; one who undercounted by half might pick 4).
  const sorted = [...CANONICAL_OUT_COUNTS]
    .filter((v) => v !== q.outs)
    .sort((a, b) => Math.abs(a - q.outs) - Math.abs(b - q.outs));
  const distractors = [sorted[0]!, sorted[1]!, sorted[sorted.length - 1]!];

  const options = shuffle([q.outs, ...distractors], rand);
  const correctIndex = options.indexOf(q.outs);

  return { ...q, options, correctIndex };
}
